import React, { useState, useEffect } from "react";
import {
    ChevronDown,
    Mail,
    Phone,
    MapPin,
    Clock,
    Send,
    CheckCircle,
} from "lucide-react";
import Footer from "../layout/Footer";
import Navbar from "../layout/Navbar";
import contactus from "../assets/new-fire-img-6.jpg";

const Contact = () => {
    const [errors, setErrors] = useState({});
    const [scrollY, setScrollY] = useState(0);
    const [isVisible, setIsVisible] = useState({});
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        company: "",
        phone: "",
        message: "",
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState("");

    useEffect(() => {
        const handleScroll = () => setScrollY(window.scrollY);
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    useEffect(() => {
        const observers = new Map();
        const elements = document.querySelectorAll("[data-animate]");

        elements.forEach((el, index) => {
            const observer = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting) {
                        setIsVisible((prev) => ({ ...prev, [index]: true }));
                    }
                },
                { threshold: 0.1 }
            );
            observer.observe(el);
            observers.set(index, observer);
        });

        return () => observers.forEach((observer) => observer.disconnect());
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = "Name is required";
        if (!formData.email.trim()) newErrors.email = "Email is required";
        if (!formData.phone.trim()) newErrors.phone = "Phone is required";
        if (!formData.company.trim()) newErrors.company = "Company is required";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        const { name, email, company, phone, message } = formData;
        const subject = encodeURIComponent(`New Contact Form ${name}`);
        const body = encodeURIComponent(
            `Name: ${name}\nEmail: ${email}\nCompany: ${company}\nPhone: ${phone}\nMessage: ${message}`
        );

        const gmailLink = `https://mail.google.com/mail/?view=cm&fs=1&to=jantacera@gmail.com&su=${subject}&body=${body}`;
        window.open(gmailLink, "_blank");

        setFormData({ name: '', email: '', company: '', phone: '', message: '' });
        setErrors({});
    };

    const contactInfo = [
        {
            icon: <Mail className="w-6 h-6" />,
            title: "Email Address",
            details: ["jantacera@gmail.com"],
        },
        {
            icon: <Phone className="w-6 h-6" />,
            title: "Phone Numbers",
            details: ["+91 94290 96444", "+91 94294 85005"],
        },
        {
            icon: <MapPin className="w-6 h-6" />,
            title: "Office Address",
            details: [
                "8-A National Highway, At Chandrapur, Wankaner,",
                "Morbi, Gujarat 363621",
            ],
        },
        {
            icon: <Clock className="w-6 h-6" />,
            title: "Business Hours",
            details: ["Mon - Fri: 9:00 AM - 6:00 PM", "Sat: 10:00 AM - 4:00 PM"],
        },
    ];

    return (
        <div className="min-h-screen bg-gray-50">
            <Navbar />

            {/* Hero Section */}
            <section className="relative h-[50vh] sm:h-[60vh] flex items-center justify-center overflow-hidden">
                <div className="absolute inset-0 z-0">
                    <img
                        src={contactus}
                        alt="Contact Background"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/60 to-transparent"></div>
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-transparent to-slate-900/40"></div>
                </div>
                <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8">
                    <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 animate-fadeInUp">
                        Get In <span className="text-orange-500">Touch</span>
                    </h1>
                    <p
                        className="text-base sm:text-lg lg:text-xl text-gray-300 mb-6 animate-fadeInUp max-w-2xl mx-auto"
                        style={{ animationDelay: "0.2s" }}
                    >
                        
                        <span className="text-orange-400 font-semibold">JANTA Refractories</span> -- Your trusted partner in high-performance refractory solutions.
                    </p>
                </div>
            </section>

            {/* Contact Form & Info */}
            <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8 bg-gray-50" data-animate>
                <div
                    className={`max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 sm:gap-16 transition-all duration-1000 ${isVisible[0] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
                        }`}
                >
                    {/* Form */}
                    <div className="bg-gray-50 rounded-2xl p-6 sm:p-8 shadow-xl border border-orange-100 hover:shadow-2xl transition-all duration-300">
                        <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">
                            Send us a Message
                        </h3>


                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Full Name
                                        <span className='text-red-700'>*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                                        placeholder="Enter your name"
                                        required
                                    />
                                    {errors.name && (
                                    <p className='text-red-500 text-md mt-1'>{errors.name}</p>
                                )}
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Email Address
                                        <span className='text-red-700'>*</span>
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                                        placeholder="Enter your email"
                                        required
                                    />
                                    {errors.email && (
                                    <p className='text-red-500 text-md mt-1'>{errors.email}</p>
                                )}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Company
                                        <span className='text-red-700'>*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="company"
                                        value={formData.company}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                                        placeholder="Company name"
                                        required
                                    />
                                    {errors.company && (
                                    <p className='text-red-500 text-md mt-1'>{errors.company}</p>
                                )}
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Phone
                                        <span className='text-red-700'>*</span>
                                    </label>
                                    <input
                                        type="tel"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                                        placeholder="Phone number"
                                        required
                                    />
                                    {errors.phone && (
                                    <p className='text-red-500 text-md mt-1'>{errors.phone}</p>
                                )}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Message
                                </label>
                                <textarea
                                    name="message"
                                    value={formData.message}
                                    onChange={handleInputChange}
                                    rows={5}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                                    placeholder="Tell us more..."
                                ></textarea>
                            </div>

                            <button
                                onClick={handleSubmit}
                                className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-orange-500 hover:to-yellow-500 disabled:opacity-60 text-white py-4 px-6 rounded-lg font-semibold transition flex items-center justify-center"
                            >
                                Send Message
                            </button>
                        </form>
                    </div>

                    {/* Contact Info */}
                    <div className="space-y-8">
                        {contactInfo.map((info, index) => (
                            <div
                                key={index}
                                className="bg-gray-50 rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-2xl transition-all border border-orange-100 hover:border-orange-300"
                            >
                                <div className="flex items-start space-x-4">
                                    <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-r from-yellow-500 to-orange-500 rounded-lg text-white">
                                        {info.icon}
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-bold text-gray-800 mb-2">
                                            {info.title}
                                        </h3>
                                        {info.details.map((detail, idx) => (
                                            <p key={idx} className="text-gray-600 text-sm sm:text-base">
                                                {detail}
                                            </p>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Map Section */}
            <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-800 px-4 sm:px-6 lg:px-8 text-center">
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
                    Visit Our <span className="text-orange-400">Office</span>
                </h2>
                <p className="text-lg sm:text-xl text-gray-300 mb-8">
                    Find us easily on the map below.
                </p>

                <div className="max-w-6xl mx-auto rounded-2xl overflow-hidden shadow-2xl border-4 border-white/10">
                    <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d11252.970479944117!2d70.9668672130882!3d22.593422082573866!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3959bde51b9b3c4f%3A0x4f817d390c7ee4c5!2sJanta%20Refractory!5e0!3m2!1sen!2sin!4v1771479769574!5m2!1sen!2sin" 
                    width="100%" 
                    height="450" 
                    style={{border:0}}
                    allowFullScreen="" 
                    loading="lazy" 
                    referrerPolicy="no-referrer-when-downgrade">
                    </iframe>
                </div>
            </section>

            <Footer />

            <style jsx>{`
                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                .animate-fadeInUp {
                    animation: fadeInUp 0.8s ease-out forwards;
                    opacity: 0;
                }
            `}</style>
        </div>
    );
};

export default Contact;
